import React, { useState, useContext, useRef } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import utilities from '../../utilities/utilities'
import AuthContext from '../../middlewares/AuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStore, faSave, faImage } from '@fortawesome/free-solid-svg-icons'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

const VALID_EXT = ['png', 'jpg', 'jpeg', 'gif', 'bmp']

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface StoreForm {
  name    : string
  email   : string
  phone   : string
  whatsapp: string
  link    : string
}

const formSchema = Yup.object().shape({
  name    : Yup.string().min(3).max(255).required(),
  email   : Yup.string().email().required(),
  phone   : Yup.string().min(3).max(255).required(),
  whatsapp: Yup.string().min(3).max(255).required(),
  link    : Yup.string().min(3).max(255).required(),
})

const INITIAL: StoreForm = { name: '', email: '', phone: '', whatsapp: '', link: '' }

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const CreateStore: React.FC = () => {
  const { getHeaders }        = useContext(AuthContext) as AuthCtx
  const { t }                 = useTranslation()
  const fileInputRef          = useRef<HTMLInputElement>(null)

  const [image, setImage]     = useState('')
  const [fileName, setFileName] = useState('')
  const [imageError, setImageError] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!VALID_EXT.includes(ext)) {
      setImage('')
      setFileName('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      Swal.fire({
        icon             : 'info',
        title            : 'Invalid extension',
        toast            : true,
        position         : 'top-end',
        showConfirmButton: false,
        timer            : 3000,
        timerProgressBar : true,
      })
      return
    }
    const base64 = await utilities.getBase64(file) as string
    setImage(base64)
    setFileName(file.name)
    setImageError(false)
  }

  const handleSubmit = async (values: StoreForm, { resetForm }: { resetForm: () => void }) => {
    if (!image) {
      setImageError(true)
      return
    }
    setImageError(false)
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post<{ response: string }>(
        `${url}stores`,
        { ...values, image },
        { headers }
      )
      Swal.fire({ icon: 'success', title: res.data.response })
      setImage('')
      setFileName('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      resetForm()
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="row justify-content-center">
      <div className="col-md-8 col-lg-6">

        {/* ── Header card ───────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          marginBottom    : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width           : 40,
              height          : 40,
              borderRadius    : 10,
              backgroundColor : `${BRAND}18`,
              display         : 'flex',
              alignItems      : 'center',
              justifyContent  : 'center',
              flexShrink      : 0,
            }}>
              <FontAwesomeIcon icon={faStore} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
              {t('stores.create.add')}
            </p>
          </div>
        </div>

        {/* ── Form card ─────────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>
            <Formik initialValues={INITIAL} validationSchema={formSchema} onSubmit={handleSubmit}>
              <Form>

                <div className="mb-4">
                  <FieldLabel>{t('stores.name')}</FieldLabel>
                  <Field type="text" name="name" className="form-control form-control-sm" />
                  <ErrorMessage name="name" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                </div>

                <div className="mb-4">
                  <FieldLabel>{t('stores.email')}</FieldLabel>
                  <Field type="email" name="email" className="form-control form-control-sm" placeholder="name@example.com" />
                  <ErrorMessage name="email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                </div>

                <div className="mb-4">
                  <FieldLabel>Whatsapp</FieldLabel>
                  <Field type="text" name="whatsapp" className="form-control form-control-sm" />
                  <ErrorMessage name="whatsapp" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                </div>

                <div className="mb-4">
                  <FieldLabel>{t('stores.create.phone')}</FieldLabel>
                  <Field type="text" name="phone" className="form-control form-control-sm" />
                  <ErrorMessage name="phone" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                </div>

                <div className="mb-4">
                  <FieldLabel>{t('stores.create.link')}</FieldLabel>
                  <Field type="text" name="link" className="form-control form-control-sm" />
                  <ErrorMessage name="link" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                </div>

                {/* ── Image picker ─────────────────────────────────── */}
                <div className="mb-4">
                  <FieldLabel>{t('stores.image')}</FieldLabel>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="d-none"
                    id="formFile"
                    accept=".png,.jpg,.jpeg,.gif,.bmp"
                    onChange={handleImage}
                  />
                  <div className="input-group">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FontAwesomeIcon icon={faImage} className="me-1" />
                      {t('stores.create.select')}
                    </button>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-light"
                      readOnly
                      value={fileName}
                      placeholder="No file selected"
                      onClick={() => fileInputRef.current?.click()}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  {imageError && (
                    <p className="text-danger small fw-bold mt-1 mb-0">{t('stores.create.required')}</p>
                  )}
                  {image && (
                    <img
                      src={image}
                      alt="preview"
                      style={{ marginTop: '0.75rem', width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                    />
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    display        : 'inline-flex',
                    alignItems     : 'center',
                    justifyContent : 'center',
                    gap            : '0.5rem',
                    width          : '100%',
                    padding        : '0.55rem 1.5rem',
                    borderRadius   : 9999,
                    backgroundColor: loading ? '#94a3b8' : BRAND,
                    color          : '#fff',
                    fontWeight     : 700,
                    fontSize       : '0.875rem',
                    border         : 'none',
                    cursor         : loading ? 'not-allowed' : 'pointer',
                    boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                    transition     : 'filter 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                >
                  {loading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <FontAwesomeIcon icon={faSave} />
                  }
                  <span>{t('stores.create.create')}</span>
                </button>

              </Form>
            </Formik>
          </div>
        </div>

      </div>
    </div>
  )
}

export default CreateStore
